import { Text } from '@revolut-internal/ui-kit'
import { TableColumn } from '@revolut-internal/ui-kit-table'

import { Security } from 'modules/security/security'
import { SelectableCell } from 'pages/Monitoring/components/SelectableCell'
import {
  QueueHealthMetricsOverview,
  ResourceFilterType,
  ResourcesSideParams,
} from 'types'
import { getSafeDurationAccessor } from 'utils/getSafeDurationAccessor'

import { AgentCountCell } from '../components/AgentCountCell'
import { CountCell } from '../components/CountCell'
import { DurationCell } from '../components/DurationCell'
import { IncidentCell } from '../components/IncidentCell'
import { ShowResourcesCell } from '../components/ShowResourcesCell'
import { QueueHealthDetailFormattedData } from '../types'

import { formatDanteLabel, toCountWithPercentage } from './format'

type ShowResourcesCellProps = {
  resourcesSideParams?: ResourcesSideParams
  onShowResources: (value: ResourcesSideParams | undefined) => void
}

export const getColumns = ({
  entityName,
  specialisationLabel,
  metrics,
  isDante,
  onCopyReport,
  ...showResourcesProps
}: ShowResourcesCellProps & {
  entityName: string
  specialisationLabel: string
  metrics?: QueueHealthMetricsOverview
  isDante: boolean
  onCopyReport: (row: QueueHealthDetailFormattedData) => void
}): TableColumn<QueueHealthDetailFormattedData>[] => [
  {
    header: specialisationLabel,
    accessorKey: 'name',
    minSize: 300,
    meta: {
      volatileCell: true,
    },
    footer: () => (metrics ? <Text variant="emphasis2">Total</Text> : '-'),

    cell: ({ row, cell }) => {
      const label = isDante ? formatDanteLabel(row.original) : row.original.name

      if (row.original.subRows) {
        return (
          <IncidentCell
            cell={cell}
            label={label}
            filterType={ResourceFilterType.Incidents}
            hasAccess={Security.canViewIncidents}
            onCopyReport={onCopyReport}
            {...showResourcesProps}
          />
        )
      }

      return (
        <SelectableCell
          cell={cell}
          onClick={e => {
            e.stopPropagation()
            showResourcesProps.onShowResources({
              filterType: ResourceFilterType.Unassigned,
              queue: row.original,
            })
          }}
        >
          {row.original.name}
        </SelectableCell>
      )
    },

    enableSorting: true,
  },
  {
    header: 'Unassigned',
    accessorKey: 'unassignedTicketCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <CountCell
        metric={metrics?.unassignedTicketCount ?? null}
        entityName={entityName}
        isGrouped
      />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.Unassigned}
        hasAccess={Security.canViewAgentTickets}
        {...showResourcesProps}
      >
        <CountCell
          metric={row.original.unassignedTicketCount}
          entityName={entityName}
          isGrouped={!!row.original.subRows}
        />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
  {
    header: 'Assigned',
    accessorKey: 'assignedTicketCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <CountCell
        metric={metrics?.assignedTicketCount ?? null}
        entityName={entityName}
        isGrouped
      />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.Assigned}
        hasAccess={Security.canViewAgentTickets}
        {...showResourcesProps}
      >
        <CountCell
          metric={row.original.assignedTicketCount}
          entityName={entityName}
          isGrouped={!!row.original.subRows}
        />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
  {
    header: 'Pending',
    accessorKey: 'pendingTicketCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <CountCell
        metric={metrics?.pendingTicketCount ?? null}
        entityName={entityName}
        isGrouped
      />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.Pending}
        hasAccess={Security.canViewAgentTickets}
        {...showResourcesProps}
      >
        <CountCell
          metric={row.original.pendingTicketCount}
          entityName={entityName}
          isGrouped={!!row.original.subRows}
        />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
  {
    header: 'Max QWT',
    id: 'maxWaiting',
    sortUndefined: -1,
    sortingFn: 'basic',
    accessorFn: ({ maxQwt }) => getSafeDurationAccessor(maxQwt.value),

    footer: () => (metrics ? <DurationCell metric={metrics.maxQwt} /> : '-'),

    cell: ({ row }) => <DurationCell metric={row.original.maxQwt} />,

    enableSorting: true,
  },
  {
    header: 'Avg QWT',
    id: 'avgQwt',
    sortUndefined: -1,
    sortingFn: 'basic',
    accessorFn: ({ avgQwt }) => getSafeDurationAccessor(avgQwt.value),

    footer: () => (metrics ? <DurationCell metric={metrics.avgQwt} /> : '-'),

    cell: ({ row }) => <DurationCell metric={row.original.avgQwt} />,

    enableSorting: true,
  },
  {
    header: 'Breached QWT',
    accessorKey: 'breachedQwtTicketCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <CountCell
        metric={
          metrics
            ? {
                value: metrics.breachedQwt.ticketCount,
                slaBreach: metrics.breachedQwt.slaBreach,
              }
            : null
        }
        entityName={entityName}
        isGrouped
      />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.BreachedQwt}
        hasAccess={Security.canViewAgentTickets}
        {...showResourcesProps}
      >
        <CountCell
          metric={row.original.breachedQwtTicketCount}
          entityName={entityName}
          isGrouped={!!row.original.subRows}
        />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
  {
    header: 'Online',
    accessorKey: 'onlineAgentCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <AgentCountCell metric={toCountWithPercentage(metrics?.onlineAgentCount)} />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.Online}
        hasAccess={Security.canViewAgentsShortAttendance}
        {...showResourcesProps}
      >
        <AgentCountCell metric={row.original.onlineAgentCount} />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
  {
    header: 'Wrapping up',
    accessorKey: 'onWrapUpAgentCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <AgentCountCell metric={toCountWithPercentage(metrics?.onWrapUpAgentCount)} />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.WrapUp}
        hasAccess={Security.canViewAgentsShortAttendance}
        {...showResourcesProps}
      >
        <AgentCountCell metric={row.original.onWrapUpAgentCount} />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
  {
    header: 'Scheduled',
    accessorKey: 'scheduledAgentCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <CountCell
        metric={metrics?.scheduledAgentCount ?? null}
        entityName="agent"
        isGrouped
      />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.Scheduled}
        hasAccess={Security.canViewAgentsShortAttendance}
        {...showResourcesProps}
      >
        <CountCell
          metric={row.original.scheduledAgentCount}
          entityName="agent"
          isGrouped={!!row.original.subRows}
        />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
  {
    header: 'Offline',
    accessorKey: 'offlineAgentCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <AgentCountCell metric={toCountWithPercentage(metrics?.offlineAgentCount)} />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.Offline}
        hasAccess={Security.canViewAgentsShortAttendance}
        {...showResourcesProps}
      >
        <AgentCountCell metric={row.original.offlineAgentCount} />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
  {
    header: 'On break',
    accessorKey: 'onBreakAgentCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <AgentCountCell metric={toCountWithPercentage(metrics?.onBreakAgentCount)} />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.OnBreak}
        hasAccess={Security.canViewAgentsShortAttendance}
        {...showResourcesProps}
      >
        <AgentCountCell metric={row.original.onBreakAgentCount} />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
  {
    header: 'Absent',
    accessorKey: 'absentAgentCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <AgentCountCell metric={toCountWithPercentage(metrics?.absentAgentCount)} />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.Absent}
        hasAccess={Security.canViewAgentsShortAttendance}
        {...showResourcesProps}
      >
        <AgentCountCell metric={row.original.absentAgentCount} />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
  {
    header: 'Overtime',
    accessorKey: 'overtimeAgentCount.value',
    meta: {
      volatileCell: true,
    },
    footer: () => (
      <AgentCountCell metric={toCountWithPercentage(metrics?.overtimeAgentCount)} />
    ),

    cell: ({ row, cell }) => (
      <ShowResourcesCell
        cell={cell}
        filterType={ResourceFilterType.Overtime}
        hasAccess={Security.canViewAgentsShortAttendance}
        {...showResourcesProps}
      >
        <AgentCountCell metric={row.original.overtimeAgentCount} />
      </ShowResourcesCell>
    ),
    enableSorting: true,
  },
]
